import React from "react";
import { Card, Table, Tooltip, Typography, Tag, Space, Row, Col, Divider, Empty } from "antd";
import Helper from "../../../core/Helper"; // Projenizdeki yol
import { t } from "i18next";
import {
    MdPayment,
    MdDateRange,
    MdStore,
    MdCreditCard,
    MdDescription,
    MdAttachMoney
} from "react-icons/md";
import { Currency } from "../../Payment/MultiplePaymentApp"; // Orijinal import
import { useUser } from "../../../core/context/UserContext";

const { Text, Title } = Typography;

export default function PaymentsPreview({ payments, appointmentCurrency }) {
    const { formatPrice } = useUser();

    // Silinmiş ödemeler listede gri olarak görünür ama toplamlara girmez
    const activePayments = (payments || []).filter(p => !p.isDeleted);

    // --- HESAPLAMALAR ---
    const totalEquivalent = activePayments.length > 0
        ? activePayments.map(x => x.currencyAmount).reduce((a, b) => a + b, 0)
        : 0;

    // Farklı para birimlerine göre toplamları grupla
    const getCurrencyTotals = () => {
        const activeCurrencies = activePayments.map(p => p.currency);
        // Currency enum'ı varsa onu kullan, yoksa veriden gelen unique currency'leri al
        const currenciesToCheck = Currency ? Object.keys(Currency).filter(k => activeCurrencies.includes(k)) : [...new Set(activeCurrencies)];

        return currenciesToCheck.map(currency => {
            const list = activePayments.filter(p => p.currency === currency);
            if (!list.length) return null;
            const total = list.map(x => x.amount).reduce((a, b) => a + b, 0);
            return { currency, total };
        }).filter(Boolean);
    };

    const currencyTotals = getCurrencyTotals();

    // --- MASAÜSTÜ TABLO KOLONLARI ---
    const columns = [
        {
            key: 'appointmentId',
            dataIndex: 'appointmentId',
            title: t('App. ID'),
            width: 80,
            render: (text, row) => (
                <Space size={4}>
                    <Tag>{text}</Tag>
                    {row.isDeleted && <Tag color="default">{t('Deleted')}</Tag>}
                </Space>
            )
        },
        {
            key: 'paymentDate',
            dataIndex: 'paymentDate',
            title: t('Date'),
            width: 200,
            render: (text) => (
                <Space>
                    <MdDateRange style={{ color: '#8c8c8c' }} />
                    {Helper.Date.dateFormat(text)}
                </Space>
            )
        },
        {
            key: 'cashRegister',
            dataIndex: 'cashRegister',
            title: t('Cash Register'),
            width: 140,
            render: (text, row) => (
                <Space>
                    <MdStore style={{ color: '#1890ff' }} />
                    {row.cashRegister?.name}
                </Space>
            )
        },
        {
            key: 'paymentMethod',
            dataIndex: 'paymentMethod',
            title: t('Method'),
            width: 140,
            render: (text, row) => (
                <Space>
                    <MdCreditCard style={{ color: '#fa8c16' }} />
                    {row.paymentMethod?.name}
                </Space>
            )
        },
        {
            key: 'description',
            dataIndex: 'description',
            title: t('Description'),
            width: 250,
            render: (text) => (
                <Tooltip title={text}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {text && <MdDescription style={{ color: '#bfbfbf' }} />}
                        <Text ellipsis style={{ maxWidth: 230 }}>{text || '-'}</Text>
                    </div>
                </Tooltip>
            )
        },
        {
            key: 'amount',
            dataIndex: 'amount',
            title: `${t('Equivalent')} (${appointmentCurrency})`,
            align: 'right',
            width: 150,
            render: (text, row) => (
                <Text strong delete={row.isDeleted} style={{ color: row.isDeleted ? '#8c8c8c' : '#389e0d' }}>
                    {formatPrice(row.currencyAmount, { currency: appointmentCurrency })}
                </Text>
            )
        },
        {
            key: 'currency',
            dataIndex: 'currency',
            title: t('Original Amount'),
            align: 'right',
            width: 150,
            render: (text, row) => (
                <Text type="secondary" delete={row.isDeleted}>
                    {formatPrice(row.amount, { currency: row?.currency })}
                </Text>
            )
        },
    ];

    return (
        <div className="payments-container">
            {/* Responsive CSS */}
            <style>{`
                .desktop-table { display: block; }
                .mobile-list { display: none; }

                .payment-row-deleted > td { background-color: #fafafa !important; opacity: .55; }
                .payment-row-deleted:hover > td { background-color: #f0f0f0 !important; }
                
                @media (max-width: 768px) {
                    .desktop-table { display: none; }
                    .mobile-list { display: block; }
                }
            `}</style>

            {/* --- DESKTOP TABLE --- */}
            <div className="desktop-table">
                <Table
                    size={'middle'}
                    dataSource={payments}
                    rowKey={'id'}
                    rowClassName={(row) => row?.isDeleted ? 'payment-row-deleted' : ''}
                    pagination={false}
                    columns={columns}
                    bordered
                    scroll={{ x: true }}
                    summary={() => (
                        <Table.Summary fixed>
                            <Table.Summary.Row style={{ backgroundColor: '#f9f9f9' }}>
                                <Table.Summary.Cell index={0} colSpan={5}>
                                    <Text strong style={{ display: 'flex', justifyContent: 'flex-end', paddingRight: 16 }}>
                                        {t('Total Payments')}:
                                    </Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={1}>
                                    <Text strong style={{ color: '#389e0d', fontSize: 15 }}>
                                        {formatPrice(totalEquivalent, { currency: appointmentCurrency || 'TRY' })}
                                    </Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={2}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', fontSize: 12 }}>
                                        {currencyTotals.map((item, idx) => (
                                            <span key={idx} style={{ color: '#8c8c8c' }}>
                                                {formatPrice(item.total, { currency: item.currency || 'TRY' })}
                                            </span>
                                        ))}
                                    </div>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    )}
                />
            </div>

            {/* --- MOBILE LIST CARD --- */}
            <div className="mobile-list">
                {payments.length === 0 && <Empty description={t('No Payments')} image={Empty.PRESENTED_IMAGE_SIMPLE} />}

                {payments.map((row) => (
                    <Card
                        key={row.id}
                        size="small"
                        style={{ marginBottom: 12, borderRadius: 8, borderLeft: `4px solid ${row.isDeleted ? '#bfbfbf' : '#389e0d'}`, backgroundColor: row.isDeleted ? '#fafafa' : undefined, opacity: row.isDeleted ? .6 : 1 }}
                        bodyStyle={{ padding: 12 }}
                    >
                        {/* Header: Tutar ve Tarih */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                            <Space size={6}>
                                <Text strong delete={row.isDeleted} style={{ fontSize: 16, color: row.isDeleted ? '#8c8c8c' : '#389e0d' }}>
                                    {formatPrice(row.currencyAmount, { currency: appointmentCurrency })}
                                </Text>
                                {row.isDeleted && <Tag color="default" style={{ margin: 0 }}>{t('Deleted')}</Tag>}
                            </Space>
                            <Space size={4} style={{ color: '#8c8c8c', fontSize: 12 }}>
                                <MdDateRange />
                                {Helper.Date.dateFormat(row.paymentDate)}
                            </Space>
                        </div>

                        <Divider style={{ margin: '8px 0' }} dashed />

                        {/* Detaylar */}
                        <Row gutter={[8, 8]} style={{ fontSize: 13 }}>
                            <Col span={12}>
                                <Space>
                                    <MdStore style={{ color: '#1890ff' }} />
                                    <Text>{row.cashRegister?.name}</Text>
                                </Space>
                            </Col>
                            <Col span={12} style={{ textAlign: 'right' }}>
                                <Space>
                                    <MdCreditCard style={{ color: '#fa8c16' }} />
                                    <Text>{row.paymentMethod?.name}</Text>
                                </Space>
                            </Col>

                            {/* Orijinal Tutar (Eğer farklıysa göster) */}
                            {row.currency !== appointmentCurrency && (
                                <Col span={24}>
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: 12, color: '#8c8c8c' }}>
                                        (Orijinal: {formatPrice(row.amount, { currency: row.currency })})
                                    </div>
                                </Col>
                            )}

                            {/* Açıklama */}
                            {row.description && (
                                <Col span={24}>
                                    <div style={{ backgroundColor: '#fafafa', padding: 6, borderRadius: 4, marginTop: 4, display: 'flex', gap: 6 }}>
                                        <MdDescription style={{ marginTop: 3, color: '#bfbfbf' }} />
                                        <Text type="secondary" style={{ fontSize: 12 }}>{row.description}</Text>
                                    </div>
                                </Col>
                            )}
                        </Row>
                    </Card>
                ))}

                {/* Mobil Özet Kutusu */}
                {payments.length > 0 && (
                    <div style={{ padding: 16, backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8, marginTop: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text strong>{t('Total Equivalent')}:</Text>
                            <Text strong style={{ fontSize: 18, color: '#389e0d' }}>
                                {formatPrice(totalEquivalent, { currency: appointmentCurrency })}
                            </Text>
                        </div>
                        {currencyTotals.length > 0 && (
                            <div style={{ marginTop: 8, textAlign: 'right', borderTop: '1px dashed #b7eb8f', paddingTop: 8 }}>
                                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>{t('Breakdown')}:</Text>
                                {currencyTotals.map((item, idx) => (
                                    <div key={idx} style={{ fontSize: 12, color: '#595959' }}>
                                        {formatPrice(item.total, { currency: item.currency })}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}